import { inject, Injectable } from '@angular/core';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { LanguageSettings } from '@common/services/language-settings';
import { TeamMembersService } from '@common/services/team-members-service';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { AppService } from '@suite/app.service';
import { catchError, concat, exhaustMap, from, of, switchMap } from 'rxjs';

import { ApplicationActions } from '../navigation/actions/application.actions';
import { TeamMembersActions } from './team-members.actions';
import { selectAllTeamMembers, selectTeamMembersGroupKeys } from './team-members.selectors';

@Injectable()
export class TeamMembersEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private teamMembersService = inject(TeamMembersService);
    private modalService = inject(ModalService);
    private languageSettings = inject(LanguageSettings);
    private appService = inject(AppService);

    loadTeamMembers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.loadTeamMembers),
            switchMap(() => {
                return concat(
                    of(TeamMembersActions.setLoaderState({ loading: true })),
                    this.teamMembersService.getTeamMembers().pipe(
                        switchMap(res => {
                            if (res.status) {
                                const users = res.data.userResults.users.map(u => {
                                    if (u.Supervisor) {
                                        u.GlobalRole = GlobalRoles.SUPERVISOR_ID;
                                    }
                                    return u;
                                });
                                const newUsers = res.data.userResults.newUsers.map(u => {
                                    if (u.Supervisor) {
                                        u.GlobalRole = GlobalRoles.SUPERVISOR_ID;
                                    }
                                    return u;
                                });
                                return of(
                                    TeamMembersActions.loadTeamMembersSuccess({
                                        users,
                                        newUsers,
                                        security: res.data.security,
                                    }),
                                    TeamMembersActions.setLoaderState({ loading: false })
                                );
                            }
                            return of(TeamMembersActions.setLoaderState({ loading: false }));
                        }),
                        catchError(() => of(TeamMembersActions.setLoaderState({ loading: false })))
                    )
                );
            })
        );
    });

    loadTeamMembersSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.loadTeamMembersSuccess),
            concatLatestFrom(() => this.store.select(selectTeamMembersGroupKeys)),
            switchMap(([, keys]) => {
                const firstKey = keys?.[0]?.value ?? '';
                return of(TeamMembersActions.setCurrentGroup({ group: firstKey }));
            })
        );
    });

    deleteTeamMember$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.deleteTeamMember),
            exhaustMap(({ user }) => {
                const modalRef = this.modalService.openModal({
                    title: this.languageSettings.PSTWebConfirmDeleteUserLabel,
                    content: this.languageSettings.PSTWebConfirmDeleteUserMessage + user.UserID,
                });
                if (!modalRef) {
                    return of(ApplicationActions.noOpAction());
                }
                return modalRef.result.pipe(
                    exhaustMap(modalRes => {
                        if (modalRes === ModalResponseCodes.OK) {
                            return concat(
                                of(TeamMembersActions.setLoaderState({ loading: true })),
                                from(this.teamMembersService.removeTeamMember(user.UserID)).pipe(
                                    switchMap(res => {
                                        if (res.status) {
                                            return of(TeamMembersActions.deleteTeamMemberSuccess({ user }), TeamMembersActions.setLoaderState({ loading: false }));
                                        }
                                        return of(TeamMembersActions.setLoaderState({ loading: false }));
                                    }),
                                    catchError(() => of(TeamMembersActions.setLoaderState({ loading: false })))
                                )
                            );
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(TeamMembersActions.setLoaderState({ loading: false })))
                );
            })
        );
    });

    updateTeamMember$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.updateTeamMember),
            exhaustMap(({ user }) => {
                return from(this.teamMembersService.updateTeamMember(user)).pipe(
                    switchMap(res => {
                        if (res.status) {
                            const updated = Object.assign(new UserStaffInfo(), res.data);
                            if (this.appService.nomWebServicesJson.HelloResults.UserStaffInfo.UserID === updated.UserID) {
                                this.appService.nomWebServicesJson.HelloResults.UserStaffInfo = Object.assign(new UserStaffInfo(), updated);
                            }
                            return of(TeamMembersActions.updateTeamMemberSuccess({ user: updated }));
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    createTeamMember$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.createTeamMember),
            concatLatestFrom(() => this.store.select(selectAllTeamMembers)),
            exhaustMap(([{ user }, allUsers]) => {
                const exists = allUsers.some(u => u.UserID.toUpperCase() === user.UserID.trim().toUpperCase());
                if (exists) {
                    this.modalService.openAlert({ title: 'Add user', content: `User with ID "${user.UserID.toUpperCase()}" already exists!`, okString: 'Ok' });
                    return of(ApplicationActions.noOpAction());
                }
                return concat(
                    of(TeamMembersActions.setLoaderState({ loading: true })),
                    from(this.teamMembersService.createTeamMember(user)).pipe(
                        switchMap(res => {
                            if (res.status) {
                                const userToAdd = Object.assign(new UserStaffInfo(), res.data);
                                return of(TeamMembersActions.createTeamMemberSuccess({ user: userToAdd }), TeamMembersActions.setLoaderState({ loading: false }));
                            }
                            return of(TeamMembersActions.setLoaderState({ loading: false }));
                        }),
                        catchError(() => of(TeamMembersActions.setLoaderState({ loading: false })))
                    )
                );
            })
        );
    });

    sendInvitation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TeamMembersActions.sendInvitation),
            exhaustMap(({ user }) => {
                return from(this.teamMembersService.inviteTeamMember(user.UserID, user.Email)).pipe(
                    switchMap(res => {
                        if (res.status) {
                            const updated = Object.assign(new UserStaffInfo(), user, { InvitationStatus: InvitationStatusCodes.PENDING });
                            return of(TeamMembersActions.updateTeamMemberSuccess({ user: updated }));
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });
}
